// External Libraries
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Assets
import FollowIdea from '../../assets/plus.png';
import ClockLogo from '../../assets/clock.png';
import Edit from '../../assets/edit.png';
import Delete from '../../assets/delete.png';
import FollowingIdea from '../../assets/tick.png';
import NoPhoto from '../../assets/no-photo.png';

// Styles
import '../../styles/IdeaCard.css';

// Redux/State Management
import { RootState } from '../../redux/store';

// API Services
import {
  deleteIdeaAPI,
  deleteUnFollowAPI,
  postFollowAPI,
} from '../../api/IdeaService';

// Types
import { IdeaEntry } from '../../types/types';

// Utilities
import { truncateText } from '../../utils/CommonUtils';

// Components
import Button from './Button';
import Text from './Text';
import ActionConfirmationModal from './ActionConfirmationModal';

interface IdeaCardProps extends IdeaEntry {
  onFollowChange?: () => void;
  onDeleteChange?: () => void;
}

const IdeaCard: React.FC<IdeaCardProps> = ({
  title,
  id,
  ideaOwner,
  ideaOwnerPhoto,
  isFollowed,
  postedDate,
  seeking,
  isMyIdea = false,
  schoolName,
  departmentName,
  ideaOwnerId,
  onFollowChange,
  onDeleteChange,
  isPrimaryOwner,
}: IdeaCardProps) => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const [isFollowState, setIsFollowState] = useState(isFollowed);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

  useEffect(() => {
    setIsFollowState(isFollowed);
  }, [isFollowed]);

  const changeFollowState = async () => {
    if (user.userDetails?.userId !== undefined) {
      try {
        if (isFollowState) {
          setIsFollowState(false);
          const unFollowResponse = await deleteUnFollowAPI(
            id,
            user.userDetails.userId,
          );
          if (!('code' in unFollowResponse) && unFollowResponse) {
            onFollowChange && onFollowChange();
          }
        } else {
          setIsFollowState(true);
          const followResponse = await postFollowAPI(
            id,
            user.userDetails.userId,
          );
          if (!('code' in followResponse) && followResponse) {
            onFollowChange && onFollowChange();
          }
        }
      } catch (error) {
        console.error('Error changing follow state:', error);
      }
    }
  };

  const handleDeleteIdea = async () => {
    setIsButtonDisabled(true);
    if (ideaOwnerId !== undefined && ideaOwnerId !== undefined) {
      // we have invoke the delete idea api here
      const deleteIdeaRes = await deleteIdeaAPI(id, ideaOwnerId);
      if ('code' in deleteIdeaRes) {
        console.error('Error delete the idea:', deleteIdeaRes);
        setError(deleteIdeaRes.message);
      } else {
        if (deleteIdeaRes) {
          setIsModalOpen(false);
          setError(null);
          onDeleteChange && onDeleteChange();
        } else {
          console.log('problem with delete this idea');
        }
      }
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setError(null);
  };

  return (
    <div className="idea-feed-card grid gap-20 p-20">
      <div className="d-flex justify-content-between align-items-center">
        <Text className="small-label d-flex align-items-center gap-8">
          <img src={ClockLogo} alt="clock" className="clock-icon" />
          {postedDate}
        </Text>

        {!isMyIdea && (
          <Button
            type="button"
            onClick={changeFollowState}
            className="follow-button"
          >
            {isFollowState ? (
              <div className="not-followed">
                <img
                  src={FollowingIdea}
                  alt="following-idea"
                  className="following-idea-img"
                />
                <Text className="following-text">Following</Text>
              </div>
            ) : (
              <div className="followed">
                <img
                  src={FollowIdea}
                  alt="follow-idea"
                  className="follow-idea-img"
                />
                <Text className="small-medium">Follow</Text>
              </div>
            )}
          </Button>
        )}
        {isMyIdea && isPrimaryOwner && (
          <div className="modify-section">
            <Button
              type="button"
              className="delete"
              onClick={() => setIsModalOpen(true)}
            >
              <img src={Delete} alt="delete" className="edit-icon" />
              <div className="input-label">Delete</div>
            </Button>
            <Button
              type="button"
              className="edit"
              onClick={() => navigate(`/my-ideas/edit/${id}`)}
            >
              <img src={Edit} alt="edit" className="edit-icon" />
              <div className="input-label">Edit</div>
            </Button>
          </div>
        )}
      </div>

      <Link to={`/ideas/${id}`} className="large-bold idea-title">
        {truncateText(title as string)}
      </Link>

      {seeking && seeking.length > 0 && (
        <div className="seeking-info">
          <Text className="input-label">
            <span className="text-small-medium">Seeking: </span>
            {seeking.join(', ')}
          </Text>
        </div>
      )}

      <div className="line"></div>

      <div className="d-flex align-items-center gap-10">
        <Link to={`/profile/${ideaOwnerId}`} className="redirect-link">
          <img
            src={ideaOwnerPhoto ? ideaOwnerPhoto : NoPhoto}
            alt={'owner-pic'}
            className="user-image"
          />
        </Link>
        <div className="grid">
          <Link to={`/profile/${ideaOwnerId}`} className="redirect-link">
            <Text className="user-name">{ideaOwner}</Text>
          </Link>
          {(schoolName || departmentName) && (
            <Text className="input-label">
              {schoolName && schoolName}
              {schoolName && departmentName && ', '}
              {departmentName && departmentName}
            </Text>
          )}
        </div>
      </div>
      <ActionConfirmationModal
        title="Are you sure you want to delete this idea?"
        message="Deleting this idea will remove it permanently from your profile, and all associated data will be deleted. This action cannot be undone."
        isFirstButtonRequired={true}
        isFirstButtonDisabled={isButtonDisabled}
        onFirstButtonAction={handleDeleteIdea}
        onFirstButtonName="Delete Idea"
        isSecondButtonRequired={true}
        onSecondButtonAction={handleCancel}
        onSecondButtonName="Cancel"
        isOpen={isModalOpen}
        error={error}
      />
    </div>
  );
};

export default IdeaCard;
